import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { api } from '../utils/api';
import DealCard from '../components/DealCard';
import PublicModal from '../components/PublicModal';
import './PublicWebsite.css';

const PublicWebsite = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('cards');

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      const response = await api.getPublicDeals();
      setDeals(response.data);
    } catch (error) {
      console.error('Error fetching deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const verifiedMOUOnly = deals.filter(d => d.mouVerified && !d.paymentVerified);
  const fullyVerified = deals.filter(d => d.mouVerified && d.paymentVerified);

  const openModal = (deal) => {
    setSelectedDeal(deal);
    setModalOpen(true);
  };

  if (loading) {
    return (
      <div className="public-website">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="public-website">
      <section className="hero-section">
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="hero-title">THE ULTIMATE COLLEGE FEST EXPERIENCE</h1>
          <p className="hero-subtitle">Where Music, Art & Energy Collide</p>
          
          <div className="hero-info">
            <div className="featured-artists">
              <h3>Featured Artists</h3>
              <div className="artists-grid">
                <div className="artist-circle">
                  <img src="/img1.jpg" alt="Artist One" className="artist-avatar" />
                  <p className="artist-name">Artist One</p>
                </div>
                <div className="artist-circle">
                  <img src="/img2.jpg" alt="Artist Two" className="artist-avatar" />
                  <p className="artist-name">Artist Two</p>
                </div>
                <div className="artist-circle">
                  <img src="/img3.jpg" alt="Artist Three" className="artist-avatar" />
                  <p className="artist-name">Artist Three</p>
                </div>
                <div className="artist-circle">
                  <img src="/img1.jpg" alt="Artist Four" className="artist-avatar" />
                  <p className="artist-name">Artist Four</p>
                </div>
              </div>
            </div>
            
            <div className="hero-description">
              <h3>About Crossroads</h3>
              <p>Crossroads is your gateway to discovering and supporting emerging talent. We connect artists, performers, and creators with opportunities that matter.</p>
              <p>Every deal is verified. Every transaction is transparent. Every artist gets their fair share.</p>
            </div>
          </div>
        </motion.div>
        <div className="hero-bg"></div>
      </section>

      <section className="deals-section">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2>MOU Verified - Payment Pending</h2>
          <p>Deals with verified MOUs awaiting payment confirmation</p>
        </motion.div>

        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
            onClick={() => setViewMode('cards')}
          >
            Cards
          </button>
          <button
            className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            Table
          </button>
        </div>

        {verifiedMOUOnly.length > 0 ? (
          viewMode === 'cards' ? (
            <div className="deals-grid">
              {verifiedMOUOnly.map((deal, index) => (
                <motion.div
                  key={deal.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <DealCard deal={deal} onClick={() => openModal(deal)} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="table-container">
              <table className="deals-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>JC/SC</th>
                    <th>Amount</th>
                    <th>POC</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {verifiedMOUOnly.map((deal) => (
                    <tr key={deal.id}>
                      <td>{deal.companyName}</td>
                      <td>{deal.jcscName}</td>
                      <td>₹{parseInt(deal.cashAmount).toLocaleString()}</td>
                      <td>{deal.pocName}</td>
                      <td>
                        <button className="view-btn" onClick={() => openModal(deal)}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <div className="empty-state">No pending deals</div>
        )}
      </section>

      <section className="deals-section verified-section">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2>Fully Verified Deals</h2>
          <p>Completed deals with verified MOUs and payments</p>
        </motion.div>

        {fullyVerified.length > 0 ? (
          viewMode === 'cards' ? (
            <div className="deals-grid">
              {fullyVerified.map((deal, index) => (
                <motion.div
                  key={deal.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <DealCard deal={deal} onClick={() => openModal(deal)} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="table-container">
              <table className="deals-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>JC/SC</th>
                    <th>Amount</th>
                    <th>POC</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {fullyVerified.map((deal) => (
                    <tr key={deal.id}>
                      <td>{deal.companyName}</td>
                      <td>{deal.jcscName}</td>
                      <td>₹{parseInt(deal.cashAmount).toLocaleString()}</td>
                      <td>{deal.pocName}</td>
                      <td>
                        <button className="view-btn" onClick={() => openModal(deal)}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <div className="empty-state">No completed deals yet</div>
        )}
      </section>

      <PublicModal isOpen={modalOpen} onClose={() => setModalOpen(false)} deal={selectedDeal} />
    </div>
  );
};

export default PublicWebsite;
